/**
 * Dev Console — Company portals (ADR-024 §4.10): every live portal, what the
 * reconciler last saw (status, DNS, certificate, its last error), its holds,
 * and the kill switch that stops EVERY portal at once.
 *
 * Holds are independent and each needs a note (audited server-side):
 *   - Admin hold — the portal refuses requests; its edge stays up.
 *   - Security hold (DNS drift / CT alert) — also takes the edge down. The
 *     reconciler places these itself on drift or a foreign certificate; only
 *     a Fias admin releases one, after checking.
 * Releasing a hold never touches another. Every placement signs everyone out
 * of that portal.
 */

import { useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type AdminCompanyWebHoldDto, type AdminCompanyWebHostDto } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/use-toast';

type HoldKind = 'admin' | 'dns_drift' | 'ct_alert';
const HOLD_LABEL: Record<AdminCompanyWebHoldDto['kind'], string> = {
  admin: 'Admin hold',
  billing: 'Billing hold',
  dns_drift: 'Security: DNS drift',
  ct_alert: 'Security: certificate alert',
};
const SELECT_CLASS =
  'h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring';

type Pending =
  | { kind: 'hold'; host: AdminCompanyWebHostDto }
  | { kind: 'release'; host: AdminCompanyWebHostDto; hold: AdminCompanyWebHoldDto }
  | { kind: 'kill-switch'; paused: boolean };

const HOSTS_PAGE = 50;

export function CompanyWebHosts() {
  const qc = useQueryClient();
  const hostPages = useInfiniteQuery({
    queryKey: queryKeys.adminCompanies.webHosts(),
    queryFn: ({ pageParam }) =>
      api.adminListCompanyWebHosts({ limit: HOSTS_PAGE, offset: pageParam }),
    initialPageParam: 0,
    // A full page means there may be more; a short one is the end.
    getNextPageParam: (last, pages) =>
      last.hosts.length === HOSTS_PAGE ? pages.length * HOSTS_PAGE : undefined,
  });
  const hosts = {
    isLoading: hostPages.isLoading,
    isError: hostPages.isError,
    data: hostPages.data ? { hosts: hostPages.data.pages.flatMap((p) => p.hosts) } : undefined,
  };
  const killSwitch = useQuery({
    queryKey: queryKeys.adminCompanies.webKillSwitch(),
    queryFn: () => api.adminGetCompanyWebKillSwitch(),
  });
  const [pending, setPending] = useState<Pending | null>(null);
  const [note, setNote] = useState('');
  const [holdKind, setHoldKind] = useState<HoldKind>('admin');

  const close = () => {
    setPending(null);
    setNote('');
    setHoldKind('admin');
  };
  const act = useMutation({
    mutationFn: async (p: Pending) => {
      if (p.kind === 'kill-switch') return api.adminSetCompanyWebKillSwitch(p.paused, note.trim());
      if (p.kind === 'hold')
        return api.adminPlaceCompanyWebHold(p.host.hostId, holdKind, note.trim());
      return api.adminReleaseCompanyWebHold(p.hold.holdId);
    },
    onSuccess: (_result, p) => {
      close();
      // Only what the action changed: the switch, or the hosts whose holds
      // moved. The rest of the Company admin plane (domains, grants, Company
      // search) is unaffected and would only be refetched for nothing.
      if (p.kind === 'kill-switch') {
        void qc.invalidateQueries({ queryKey: queryKeys.adminCompanies.webKillSwitch() });
        return;
      }
      const hostId = p.kind === 'hold' ? p.host.hostId : p.hold.hostId;
      void qc.invalidateQueries({ queryKey: queryKeys.adminCompanies.webHosts() });
      void qc.invalidateQueries({ queryKey: queryKeys.adminCompanies.webHost(hostId) });
    },
    onError: (err) =>
      toast({
        title: 'Could not save',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      }),
  });

  const paused = killSwitch.data?.killSwitch.paused === true;
  const needsNote = pending?.kind !== 'release';

  return (
    <>
      <h2 className="mb-3 mt-8 text-lg font-semibold text-foreground">Portals</h2>
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            All Company portals
            {killSwitch.data && (
              <Badge variant={paused ? 'destructive' : 'secondary'}>
                {paused ? 'Stopped' : 'Serving'}
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            Stops every portal at once and signs everyone out of them. fias.io is not affected.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {paused && killSwitch.data?.killSwitch.reason && (
            <p className="text-sm text-muted-foreground">
              Reason: {killSwitch.data.killSwitch.reason}
            </p>
          )}
          <Button
            variant={paused ? 'default' : 'destructive'}
            disabled={!killSwitch.data}
            onClick={() => setPending({ kind: 'kill-switch', paused: !paused })}
          >
            {paused ? 'Restart all portals' : 'Stop all portals'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Portals</CardTitle>
          <CardDescription>What the reconciler last saw at the edge.</CardDescription>
        </CardHeader>
        <CardContent>
          {hosts.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : hosts.isError ? (
            <p role="alert" className="text-sm text-destructive">
              Could not load portals.
            </p>
          ) : (hosts.data?.hosts.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">No Company portals yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company / portal</TableHead>
                  <TableHead>Edge</TableHead>
                  <TableHead>Holds</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {hosts.data!.hosts.map((h) => (
                  <TableRow key={h.hostId}>
                    <TableCell>
                      <div className="font-medium">{h.hostname}</div>
                      <div className="text-xs text-muted-foreground">
                        {h.companyName} · {h.displayLabel} · {h.accessPolicy}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-xs space-y-1 text-xs">
                      <Badge variant={h.status === 'active' ? 'default' : 'secondary'}>
                        {h.status}
                      </Badge>
                      <div>
                        DNS {h.dnsStatus} · cert {h.certStatus}
                        {h.certFailureReason ? ` (${h.certFailureReason})` : ''} · tenant{' '}
                        {h.providerEnabled === null ? '—' : h.providerEnabled ? 'on' : 'off'}
                      </div>
                      {h.lastError && <div className="text-destructive">{h.lastError}</div>}
                    </TableCell>
                    <TableCell className="space-y-1">
                      {(h.openHolds ?? []).length === 0 && (
                        <span className="text-xs text-muted-foreground">None</span>
                      )}
                      {(h.openHolds ?? []).map((hold) => (
                        <div key={hold.holdId} className="flex items-center gap-2 text-xs">
                          <Badge variant="destructive">{HOLD_LABEL[hold.kind]}</Badge>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setPending({ kind: 'release', host: h, hold })}
                          >
                            Release
                          </Button>
                          {hold.note && (
                            <span className="text-muted-foreground" title={hold.note}>
                              {hold.note.length > 40 ? `${hold.note.slice(0, 40)}…` : hold.note}
                            </span>
                          )}
                        </div>
                      ))}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setPending({ kind: 'hold', host: h })}
                      >
                        Place hold
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {hostPages.hasNextPage && (
            <Button
              className="mt-3"
              size="sm"
              variant="outline"
              disabled={hostPages.isFetchingNextPage}
              onClick={() => void hostPages.fetchNextPage()}
            >
              {hostPages.isFetchingNextPage ? 'Loading…' : 'Show more'}
            </Button>
          )}
        </CardContent>
      </Card>

      <Dialog open={pending !== null} onOpenChange={(open) => !open && close()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{pending ? dialogTitle(pending) : ''}</DialogTitle>
            <DialogDescription>
              {pending?.kind === 'release'
                ? 'Only this hold is released. Other holds stay in place.'
                : 'Everyone signed in to the affected portals is signed out. The note is recorded in the security audit log.'}
            </DialogDescription>
          </DialogHeader>
          {pending?.kind === 'hold' && (
            <div className="grid gap-1.5">
              <Label htmlFor="company-web-hold-kind">Kind</Label>
              <select
                id="company-web-hold-kind"
                className={SELECT_CLASS}
                value={holdKind}
                onChange={(e) => setHoldKind(e.target.value as HoldKind)}
              >
                <option value="admin">Admin hold — refuse requests, edge stays up</option>
                <option value="dns_drift">Security: DNS drift — take the edge down</option>
                <option value="ct_alert">Security: certificate alert — take the edge down</option>
              </select>
            </div>
          )}
          {needsNote && (
            <div className="grid gap-1.5">
              <Label htmlFor="company-web-note">
                {pending?.kind === 'kill-switch' ? 'Reason' : 'Note'}
              </Label>
              <Textarea
                id="company-web-note"
                value={note}
                maxLength={500}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button
              disabled={(needsNote && note.trim().length === 0) || act.isPending}
              onClick={() => pending && act.mutate(pending)}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function dialogTitle(p: Pending): string {
  if (p.kind === 'kill-switch') return p.paused ? 'Stop all portals?' : 'Restart all portals?';
  if (p.kind === 'hold') return `Place a hold on ${p.host.hostname}?`;
  return `Release ${HOLD_LABEL[p.hold.kind].toLowerCase()} on ${p.host.hostname}?`;
}
